import { type DependencyMapNode } from '@ardoq/dependency-map';
import { isTruthy } from '@ardoq/common-helpers';
import { getScenarioGroup } from 'tabview/graphViews/traversalViewModel';
import { LoadedScenarioData } from 'loadedScenarioData$';
import { ShowRelatedComponentsShape } from '@ardoq/data-model';
import { DependencyMapGroupingData } from './types';
import { getAppliedGroups } from '@ardoq/graph';
import type {
  APIOrganizationUser,
  FormattingFilter,
  GroupingRule,
} from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import type { I18nLocale } from '@ardoq/i18n';
import {
  getActiveConditionalFormattingForLegendPure,
  type ActiveConditionalFormattingForLegend,
} from '@ardoq/view-legend';

export const getDirectReferenceNodes = ({
  incomingReferences,
  outgoingReferences,
}: DependencyMapNode) =>
  [incomingReferences, outgoingReferences]
    .filter(isTruthy)
    .flat()
    .map(({ node }) => node);

export const resolveLegendConditionalFormatting = (
  scopeData: EnhancedScopeData,
  formattingFilters: FormattingFilter[],
  users: APIOrganizationUser[],
  i18nLocale: I18nLocale,
  isNewConditionalFormatting: boolean
): ActiveConditionalFormattingForLegend =>
  getActiveConditionalFormattingForLegendPure(
    { ...scopeData, organizationUsers: users },
    formattingFilters,
    i18nLocale,
    isNewConditionalFormatting
  );

type GetDependencyMapGroupingDataArgs = {
  loadedScenarioData: LoadedScenarioData;
  showScopeRelated: boolean;
  scenarioRelatedComponents: ShowRelatedComponentsShape;
  groupingRules: GroupingRule[];
};
export const getDependencyMapGroupingData = ({
  loadedScenarioData,
  showScopeRelated,
  scenarioRelatedComponents,
  groupingRules,
}: GetDependencyMapGroupingDataArgs): DependencyMapGroupingData => {
  const scenarioGroup = getScenarioGroup({
    loadedScenarioData,
    showScopeRelated,
    scenarioRelatedComponentIds: scenarioRelatedComponents.componentIds,
  });

  const userDefinedGroupingRules = [scenarioGroup, ...groupingRules].filter(
    isTruthy
  );

  const hasUserDefinedReferenceTypeGroups = userDefinedGroupingRules.some(
    rule => rule.type === 'REFERENCE' || rule.type === 'REFERENCE_TYPE_NAME'
  );

  return {
    appliedGroupingRules: getAppliedGroups(userDefinedGroupingRules),
    supportedUserDefinedGroupingRules: userDefinedGroupingRules,
    hasUserDefinedReferenceTypeGroups,
  };
};
